import { Injectable } from '@angular/core';
import { ComponentBase } from '../component-base';
import { StageService } from './stage.service';
import { SceneUtilsService } from './scene-utils.service';
import { MoveCameraService } from '../Component(2D and 3D)/2D/move-camera.service';
import { AppSettingsService } from './app-settings.service';
import { RailingGenerationService } from './railing/services/railing-generation.service';
import { SizeHelperService } from './size-helper.service';
import { OrbitService } from './orbit.service';
import { AutoScaleSprite } from '../custom/AutoScaleSprite';

declare let THREE: any;

// DockRail rail-length modifier (spec pp.16-18): arrow sprites at the OPEN
// ends of each railing run (ends not meeting a neighbouring railed side at a
// corner) let the user drag the start/stop of the run along its edge. Offsets
// are written to floor.userData.points[i].railStart / .railStop (cm from the
// edge's first point) and consumed by
// RailingCalculationHelperService.applyRailRunLimits. Dragging an arrow so far
// that less than one 60" section remains removes the railing from that edge —
// its anchor (DockRailAnchorService) then reappears and restores the full run
// on click.
@Injectable()
export class RailLengthModifierService extends ComponentBase {
  private sprites = [];
  private loader = new THREE.TextureLoader();
  private raycaster = new THREE.Raycaster();
  private dragPlane = new THREE.Plane();
  private currentSprite: any = null;
  private oldPosition: any = null;
  private buildSubscription: any = null;
  // loaded once — drawSprites runs on every railing rebuild and THREE.Cache is off
  private spriteMap: any = null;

  constructor(
    private stage: StageService,
    private sceneUtils: SceneUtilsService,
    private moveCameraService: MoveCameraService,
    private appSettingsService: AppSettingsService,
    private railingGenerationService: RailingGenerationService,
    private sizeHelper: SizeHelperService,
    private orbitService: OrbitService
  ) {
    super();
    this.disable();
  }

  enable() {
    super.enable();
    this.drawSprites();
    if (!this.buildSubscription || this.buildSubscription.closed) {
      this.buildSubscription = this.railingGenerationService.build.subscribe(() => {
        if (this.isEnabled() && !this.currentSprite) {
          this.drawSprites();
        }
      });
    }
  }

  disable() {
    this.removeSprites();
    if (this.buildSubscription) {
      this.buildSubscription.unsubscribe();
      this.buildSubscription = null;
    }
    super.disable();
  }

  private getMainFloor() {
    const compound = this.stage.getCompoundFloor();
    if (!compound || !compound.children || !compound.children.length) {
      return null;
    }
    return compound.children.find(f => f.userData.mainFloor) || compound.children[0];
  }

  // positive = counter-clockwise in the floor's x/y (world x/z) plane
  private polygonSignedArea(points): number {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const n = points[(i + 1) % points.length];
      area += p.x * n.y - n.x * p.y;
    }
    return area / 2;
  }

  // a side has a railing run unless it carries a wall/tier, its anchor hasn't
  // been clicked (noRail) or its run was dragged below one 60" section.
  // Mirrors applyRailRunLimits: the 60" minimum only applies when a run limit
  // is set — an unlimited side always builds, even if shorter than 60"
  // (e.g. the 150cm ends of the straight dock)
  private edgeHasRun(points, i): boolean {
    const point = points[i];
    if (point.superstructureType || point.noRail) {
      return false;
    }
    if (!point.railStart && !point.railStop) {
      return true;
    }
    const next = points[(i + 1) % points.length];
    const length = Math.sqrt(
      Math.pow(next.x - point.x, 2) + Math.pow(next.y - point.y, 2)
    );
    return length - (point.railStart || 0) - (point.railStop || 0) >= this.sizeHelper.INCHToCM(60);
  }

  drawSprites(): void {
    this.removeSprites();
    const floor = this.getMainFloor();
    if (!floor || !floor.userData.points) {
      return;
    }

    const height = floor.getWorldPosition(new THREE.Vector3()).y + 5;
    const points = floor.userData.points;
    if (!this.spriteMap) {
      this.spriteMap = this.loader.load('../../assets/images/sprites/dock_rail_length_arrow.png');
    }
    const ccw = this.polygonSignedArea(points) > 0;

    for (let i = 0; i < points.length; i++) {
      // arrows only on open run ends (spec pp.16-18): an end that meets a
      // neighbouring railed side at a corner is fixed and gets no arrow
      if (!this.edgeHasRun(points, i)) {
        continue;
      }
      const prevHasRun = this.edgeHasRun(points, (i - 1 + points.length) % points.length);
      const nextHasRun = this.edgeHasRun(points, (i + 1) % points.length);
      if (prevHasRun && nextHasRun) {
        continue;
      }

      const fPoint = points[i];
      const sPoint = points[(i + 1) % points.length];

      const dir = new THREE.Vector3(sPoint.x - fPoint.x, 0, sPoint.y - fPoint.y);
      const edgeLength = dir.length();
      dir.normalize();

      // arrow sits outside the dock edge, next to the run's end post
      const outward = (ccw
        ? new THREE.Vector3(dir.z, 0, -dir.x)
        : new THREE.Vector3(-dir.z, 0, dir.x)
      ).multiplyScalar(45);

      const railStart = fPoint.railStart || 0;
      const railStop = fPoint.railStop || 0;

      const spriteMaterial = new THREE.SpriteMaterial({
        map: this.spriteMap,
        color: 0xffffff,
        depthTest: false,
        depthWrite: false
      });

      const makeSprite = (distanceAlongEdge, isStart) => {
        const sprite = new AutoScaleSprite(56, 28, spriteMaterial.clone());
        sprite.position.set(
          fPoint.x + dir.x * distanceAlongEdge + outward.x,
          height,
          fPoint.y + dir.z * distanceAlongEdge + outward.z
        );
        sprite.name = isStart ? 'Rail start grip' : 'Rail stop grip';
        sprite.userData.floor = floor;
        sprite.userData.pointIndex = i;
        sprite.userData.isStart = isStart;
        sprite.userData.edgeLength = edgeLength;
        sprite.userData.edgeDir = dir.clone();
        sprite.userData.edgeOrigin = new THREE.Vector3(fPoint.x, height, fPoint.y);
        sprite.userData.outward = outward.clone();

        // keep the arrow icon aligned with the on-screen direction of the
        // edge — recomputed every frame so it follows camera orbit/rotation
        const edgeA = new THREE.Vector3(fPoint.x, height, fPoint.y);
        const edgeB = new THREE.Vector3(sPoint.x, height, sPoint.y);
        const screenA = new THREE.Vector3();
        const screenB = new THREE.Vector3();
        const autoScale = sprite.onBeforeRender;
        sprite.onBeforeRender = (renderer, scene, camera) => {
          autoScale.call(sprite, renderer, scene, camera);
          screenA.copy(edgeA).project(camera);
          screenB.copy(edgeB).project(camera);
          const size = renderer.getSize();
          // NDC y is up, screen y is down — invert it
          const dx = (screenB.x - screenA.x) * size.width;
          const dy = (screenA.y - screenB.y) * size.height;
          sprite.material.rotation = -Math.atan2(dy, dx);
        };

        this.sprites.push(sprite);
        this.stage.add(sprite);
      };

      if (!prevHasRun) {
        makeSprite(railStart, true);
      }
      if (!nextHasRun) {
        makeSprite(edgeLength - railStop, false);
      }
    }
  }

  removeSprites() {
    if (this.sprites.length) {
      this.stage.removeMany(this.sprites);
      this.sprites = [];
    }
  }

  private intersectDragPlane(e: MouseEvent) {
    const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
    this.raycaster.ray.origin.copy(p.ray.origin);
    this.raycaster.ray.direction.copy(p.ray.direction);
    const intersection = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.dragPlane, intersection)
      ? intersection
      : null;
  }

  mouseDown(e: MouseEvent) {
    if (!this.sprites.length) {
      return;
    }
    const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
    this.raycaster.ray.origin.copy(p.ray.origin);
    this.raycaster.ray.direction.copy(p.ray.direction);

    const intersection = this.raycaster.intersectObjects(this.sprites);
    if (intersection.length) {
      this.currentSprite = intersection[0].object;
      this.dragPlane.set(new THREE.Vector3(0, 1, 0), -this.currentSprite.position.y);
      this.oldPosition = this.intersectDragPlane(e);
      if (this.appSettingsService.is2D) {
        this.moveCameraService.disable();
      }
      this.orbitService.disable();
      return false;
    }
  }

  mouseMove(e: MouseEvent) {
    if (!this.currentSprite || !this.oldPosition) {
      return;
    }
    e.stopPropagation();

    const newPosition = this.intersectDragPlane(e);
    if (!newPosition) {
      return;
    }

    const move = newPosition.clone().sub(this.oldPosition);
    const { edgeDir, edgeLength, edgeOrigin, outward, isStart, floor, pointIndex } = this.currentSprite.userData;
    const shift = move.projectOnVector(edgeDir);

    const point = floor.userData.points[pointIndex];
    const railStart = point.railStart || 0;
    const railStop = point.railStop || 0;

    const signedShift = shift.dot(edgeDir);
    if (isStart) {
      let next = railStart + signedShift;
      next = Math.max(0, Math.min(next, edgeLength - railStop));
      point.railStart = next;
      this.currentSprite.position.copy(
        edgeOrigin.clone().add(edgeDir.clone().multiplyScalar(next)).add(outward)
      );
    } else {
      let next = railStop - signedShift;
      next = Math.max(0, Math.min(next, edgeLength - railStart));
      point.railStop = next;
      this.currentSprite.position.copy(
        edgeOrigin.clone().add(edgeDir.clone().multiplyScalar(edgeLength - next)).add(outward)
      );
    }

    this.oldPosition = newPosition;
  }

  mouseUp() {
    if (!this.currentSprite) {
      return;
    }
    const point = this.currentSprite.userData.floor.userData.points[
      this.currentSprite.userData.pointIndex
    ];
    // snap tiny offsets back to a full run
    if (point.railStart && point.railStart < 10) {
      point.railStart = 0;
    }
    if (point.railStop && point.railStop < 10) {
      point.railStop = 0;
    }
    this.currentSprite = null;
    this.oldPosition = null;
    if (this.appSettingsService.is2D) {
      this.moveCameraService.enable();
    }
    // mouseDown disabled orbit unconditionally — restore it the same way,
    // otherwise a drag in 2D leaves orbit dead after switching to 3D
    this.orbitService.enable();
    this.railingGenerationService.buildRailing(this.railingGenerationService.IsVisible());
    this.drawSprites();
    // the drag consumed this mouseUp — keep Select3dService's release handler
    // from clearing the current tier selection
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/rail-length-modifier.service.ts